// src/pages/LoginMfaStep.jsx
//
// The second step of sign-in for users with two-step verification on. Login.jsx
// renders it inside the same card after POST /auth/login answers
// { mfa_required, challenge }. The challenge lives only in React state; it is
// never written to storage and never logged.
import React, { useState } from 'react';
import { AlertCircle, ArrowLeft, KeyRound, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import { Input, Label } from '@/components/ui/input.jsx';
import { apiCall } from '../utils/api';
import {
  challengeExpired, isCompleteCode, mfaErrorMessage,
  sanitizeRecoveryInput, sanitizeTotpInput,
} from '../lib/mfa';

export function MfaStepForm({
  mode, code, error, isLoading, onCodeChange, onToggleMode, onSubmit, onStartOver,
}) {
  const recovery = mode === 'recovery';
  const ready = isCompleteCode(code, mode);
  return (
    <form onSubmit={onSubmit} className="space-y-6" aria-label="Two-step verification">
      {error && (
        <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-3" role="alert">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm text-red-400">{error}</p>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <Label htmlFor="mfa-code">{recovery ? 'Recovery code' : 'Verification code'}</Label>
        <div className="relative">
          {recovery ? (
            <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6a6a6a]" />
          ) : (
            <ShieldCheck className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6a6a6a]" />
          )}
          <Input
            id="mfa-code"
            type="text"
            inputMode={recovery ? 'text' : 'numeric'}
            autoComplete={recovery ? 'off' : 'one-time-code'}
            autoCapitalize="characters"
            spellCheck={false}
            autoFocus
            placeholder={recovery ? 'XXXX-XXXX-XXXX-XXXX' : '123456'}
            value={code}
            onChange={(e) => onCodeChange(e.target.value)}
            className={`pl-10 font-mono ${recovery ? '' : 'tracking-[0.4em]'}`}
            disabled={isLoading}
            required
          />
        </div>
        <p className="text-xs text-[#6a6a6a]">
          {recovery
            ? 'Each recovery code works once. You will be able to make new ones in Settings.'
            : 'Open your authenticator app and enter the 6-digit code for Connex.'}
        </p>
      </div>

      <Button type="submit" className="w-full" disabled={isLoading || !ready}>
        {isLoading ? 'Verifying...' : 'Verify and sign in'}
      </Button>

      <div className="flex items-center justify-between text-sm">
        <button
          type="button"
          onClick={onStartOver}
          className="flex items-center gap-1 text-[#a0a0a0] hover:text-white transition-colors"
          disabled={isLoading}
        >
          <ArrowLeft className="w-4 h-4" />
          Start over
        </button>
        <button
          type="button"
          onClick={onToggleMode}
          className="text-blue-400 hover:text-blue-300 transition-colors"
          disabled={isLoading}
        >
          {recovery ? 'Use my authenticator app' : 'Use a recovery code'}
        </button>
      </div>
    </form>
  );
}

export default function LoginMfaStep({ challenge, onSignedIn, onStartOver }) {
  const [mode, setMode] = useState('totp');
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleCodeChange = (raw) => {
    setCode(mode === 'recovery' ? sanitizeRecoveryInput(raw) : sanitizeTotpInput(raw));
  };

  const handleToggleMode = () => {
    setMode(mode === 'recovery' ? 'totp' : 'recovery');
    setCode('');
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (challengeExpired(challenge.issuedAt)) {
      onStartOver('Your sign-in timed out. Enter your password again to get a fresh start.');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const res = await apiCall('/auth/mfa/verify', {
        method: 'POST',
        body: JSON.stringify({ challenge: challenge.value, code }),
      });
      if (res.ok) {
        const body = await res.json();
        onSignedIn(body);
        return;
      }
      const errorData = await res.json().catch(() => ({}));
      setError(mfaErrorMessage(res.status, errorData.detail));
      setCode('');
    } catch (err) {
      console.error('Verification request failed', err);
      setError('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <MfaStepForm
      mode={mode}
      code={code}
      error={error}
      isLoading={isLoading}
      onCodeChange={handleCodeChange}
      onToggleMode={handleToggleMode}
      onSubmit={handleSubmit}
      onStartOver={() => onStartOver('')}
    />
  );
}
